/**
 * MultiSelect Component
 * Dropdown with checkbox list for selecting multiple items
 */

import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronDown, Search, Check } from 'lucide-react';
import { Input } from '../../ui/Input';

interface MultiSelectProps<T> {
  items: T[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  getItemId: (item: T) => string;
  getItemLabel: (item: T) => string;
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
}

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled.button<{ $isOpen: boolean; $disabled: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ $isOpen, theme }) =>
    $isOpen &&
    `
    border-color: ${theme.colors.primary.ocean};
    box-shadow: 0 0 0 1px ${theme.colors.primary.ocean};
  `}

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary.ocean};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.ocean};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.primary.ocean};
  }
`;

const SelectText = styled.span<{ $hasSelection: boolean }>`
  color: ${({ $hasSelection, theme }) =>
    $hasSelection ? theme.colors.text.primary : theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChevronIcon = styled(ChevronDown)<{ $isOpen: boolean }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: transform ${({ theme }) => theme.transitions.fast};
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  max-height: 300px;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  overflow: hidden;
`;

const SearchContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const OptionsHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.backgrounds.gray};
`;

const SelectionCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const HeaderAction = styled.button`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary.ocean};
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[1]};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary.navy};
    text-decoration: underline;
  }
`;

const OptionsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[2]};

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.borders.default};
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.borders.dark};
    }
  }
`;

const OptionItem = styled.label<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.primary.lightSky + '20' : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary.ocean};
`;

const OptionLabel = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CheckIcon = styled(Check)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.primary.ocean};
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[6]} ${({ theme }) => theme.spacing[3]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

export function MultiSelect<T>({
  items,
  selectedIds,
  onChange,
  getItemId,
  getItemLabel,
  placeholder = 'Select items...',
  searchable = true,
  disabled = false,
}: MultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Filter items based on search
  const filteredItems = searchQuery
    ? items.filter((item) => getItemLabel(item).toLowerCase().includes(searchQuery.toLowerCase()))
    : items;

  // Handle item toggle
  const toggleItem = (itemId: string) => {
    const isSelected = selectedIds.includes(itemId);
    if (isSelected) {
      onChange(selectedIds.filter((id) => id !== itemId));
    } else {
      onChange([...selectedIds, itemId]);
    }
  };

  // Handle select all
  const selectAll = () => {
    const allIds = filteredItems.map(getItemId);
    const newSelected = new Set([...selectedIds, ...allIds]);
    onChange(Array.from(newSelected));
  };

  // Handle deselect all
  const deselectAll = () => {
    const filteredIds = new Set(filteredItems.map(getItemId));
    onChange(selectedIds.filter((id) => !filteredIds.has(id)));
  };

  // Get display text
  const getDisplayText = () => {
    if (selectedIds.length === 0) return placeholder;
    if (selectedIds.length === 1) {
      const item = items.find((i) => getItemId(i) === selectedIds[0]);
      return item ? getItemLabel(item) : `${selectedIds.length} selected`;
    }
    return `${selectedIds.length} selected`;
  };

  return (
    <Container ref={containerRef}>
      <SelectButton
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        $isOpen={isOpen}
        $disabled={disabled}
        disabled={disabled}
      >
        <SelectText $hasSelection={selectedIds.length > 0}>{getDisplayText()}</SelectText>
        <ChevronIcon $isOpen={isOpen} />
      </SelectButton>

      <Dropdown $isOpen={isOpen}>
        {searchable && (
          <SearchContainer>
            <Input
              type="search"
              placeholder="Search..."
              leftIcon={<Search />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              autoFocus
            />
          </SearchContainer>
        )}

        <OptionsHeader>
          <SelectionCount>
            {selectedIds.length} of {items.length} selected
          </SelectionCount>
          <div style={{ display: 'flex', gap: '8px' }}>
            <HeaderAction type="button" onClick={selectAll}>
              Select All
            </HeaderAction>
            <HeaderAction type="button" onClick={deselectAll}>
              Clear
            </HeaderAction>
          </div>
        </OptionsHeader>

        <OptionsList>
          {filteredItems.length === 0 ? (
            <EmptyState>No items found</EmptyState>
          ) : (
            filteredItems.map((item) => {
              const itemId = getItemId(item);
              const isSelected = selectedIds.includes(itemId);

              return (
                <OptionItem key={itemId} $isSelected={isSelected}>
                  <Checkbox checked={isSelected} onChange={() => toggleItem(itemId)} />
                  <OptionLabel>{getItemLabel(item)}</OptionLabel>
                  {isSelected && <CheckIcon />}
                </OptionItem>
              );
            })
          )}
        </OptionsList>
      </Dropdown>
    </Container>
  );
}

export default MultiSelect;
